import axios from 'axios';
import type { AdminClassPlan, AdminUser, BillingInfo, Class, ClassInvitePreview, Lesson, LessonAssignment, LessonAssignmentCreate, LessonAssignmentReorder, LessonAssignmentUpdate, LessonCreate, LessonDraft, LessonIssueReport, LessonStatus, LessonSummary, LessonUpdate, SharedSubmission, SimulationAllowance, StudentProgress, Submission, SubmissionCreate, SubmissionWithStudent, User } from '../types/api';

export type { AdminUser, User } from '../types/api';

// Use environment variable if set, otherwise construct from current window location
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // If running in browser, use the same host as the frontend but with port 7000
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:7000`;
  }

  // Fallback for SSR or other environments
  return 'http://localhost:7000';
};

export const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// AuthContext registers here, so an expired token anywhere in the app signs the user out (and ProtectedRoute
// sends them to /login?next=…) instead of leaving every page to fail on its own.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config;
    const sentToken = Boolean(config?.headers?.Authorization);
    // A wrong password on the login form is a 401 too, but it is not an expired session.
    const isLogin = typeof config?.url === 'string' && config.url.includes('/api/auth/login');
    if (error?.response?.status === 401 && sentToken && !isLogin) {
      localStorage.removeItem('token');
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  }
);

/**
 * A message for the user from any failed request. FastAPI sends `detail` as a string for HTTPException and as a
 * list of {loc, msg, …} for validation errors (422), which must not be rendered as React children.
 */
export function getErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Can't reach the server. Check your connection and try again.";
    }
    const detail = (error.response.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail) {
      return detail;
    }
    // Errors with a code for the app to act on: {code, message, ...} (e.g. class_full, simulation_time_used_up).
    if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof (detail as { message?: unknown }).message === 'string') {
      return (detail as { message: string }).message;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((item) => {
          const msg = typeof item?.msg === 'string' ? item.msg.replace(/^Value error, /, '') : String(item);
          const field = Array.isArray(item?.loc) ? item.loc.filter((part: unknown) => part !== 'body').join('.') : '';
          return field ? `${field}: ${msg}` : msg;
        })
        .join('; ');
    }
    return fallback;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export function getErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

// The `code` of an error the app acts on ({detail: {code, message}}), e.g. email_not_confirmed (ACC-16).
export function getErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  const code = detail && typeof detail === 'object' && !Array.isArray(detail) ? (detail as { code?: unknown }).code : undefined;
  return typeof code === 'string' ? code : undefined;
}

// Must match the backend's minimum (register and admin user creation).
export const MIN_PASSWORD_LENGTH = 8;

export interface LoginData {
  username: string;  // API expects username field for email
  password: string;
}

export interface RegisterData {
  email: string;
  full_name: string;
  password: string;
  role: 'teacher' | 'student';
  // The unticked newsletter box (GDPR: opt-in; Brevo then asks them to confirm by email).
  newsletter_opt_in?: boolean;
  // A class invite being followed (CLS-9): it rides along in the confirmation link (ACC-16).
  invite_code?: string | null;
}

export const authService = {
  async login(data: LoginData) {
    const formData = new FormData();
    formData.append('username', data.username);
    formData.append('password', data.password);

    const response = await api.post('/api/auth/login', formData);
    localStorage.setItem('token', response.data.access_token);
    return response.data;
  },

  async register(data: RegisterData): Promise<User> {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },

  // Email confirmation (ACC-16): the link's token; confirming doesn't log in (the password comes next).
  async confirmEmail(token: string): Promise<{ status: 'confirmed' | 'already_confirmed'; email: string }> {
    const response = await api.post('/api/auth/confirmation/confirm', { token });
    return response.data;
  },

  // The same answer whether or not the address has an account waiting (nothing to learn about other people).
  async resendConfirmation(email: string, inviteCode?: string | null): Promise<string> {
    const response = await api.post('/api/auth/confirmation/resend', { email, invite_code: inviteCode || undefined });
    return response.data.message;
  },

  // A mistyped address at registration, proven by the password; the new link goes to the new address.
  async changeUnconfirmedEmail(email: string, password: string, newEmail: string, inviteCode?: string | null): Promise<string> {
    const response = await api.post('/api/auth/confirmation/change-email', {
      email, password, new_email: newEmail, invite_code: inviteCode || undefined,
    });
    return response.data.email;
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  // Account settings: the name and the newsletter consent.
  async updateProfile(data: { full_name?: string; newsletter_opt_in?: boolean }): Promise<User> {
    const response = await api.patch('/api/auth/me', data);
    return response.data;
  },

  // A new password ends every login of the account (ACC-9); the answer is a new token for this browser.
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await api.post('/api/auth/password', { current_password: currentPassword, new_password: newPassword });
    if (response.data?.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
  },

  // Download my data (ACC-15): everything stored about the logged-in user, as a JSON file.
  async exportMyData(): Promise<Blob> {
    const response = await api.get('/api/auth/me/export', { responseType: 'blob' });
    return response.data;
  },

  logout() {
    localStorage.removeItem('token');
  },

  isAuthenticated() {
    return !!localStorage.getItem('token');
  },
};

export const classService = {
  async getClasses(): Promise<Class[]> {
    const response = await api.get('/api/classes/');
    return response.data;
  },

  async createClass(data: { name: string; description?: string; lesson_ids?: number[] }): Promise<Class> {
    const response = await api.post('/api/classes/', data);
    return response.data;
  },

  async updateClass(classId: number, data: { name?: string; description?: string }): Promise<Class> {
    const response = await api.patch(`/api/classes/${classId}`, data);
    return response.data;
  },

  async deleteClass(classId: number): Promise<void> {
    await api.delete(`/api/classes/${classId}`);
  },

  async removeStudent(classId: number, studentId: number): Promise<Class> {
    const response = await api.delete(`/api/classes/${classId}/students/${studentId}`);
    return response.data;
  },

  // Replaces the invite code, so links and codes shared earlier stop working.
  async regenerateInviteCode(classId: number): Promise<Class> {
    const response = await api.post(`/api/classes/${classId}/invite-code`);
    return response.data;
  },

  async getClassByInviteCode(inviteCode: string): Promise<ClassInvitePreview> {
    const response = await api.get(`/api/classes/invite/${encodeURIComponent(inviteCode)}`);
    return response.data;
  },

  async joinClassByInviteCode(inviteCode: string): Promise<Class> {
    const response = await api.post('/api/classes/join', { invite_code: inviteCode });
    return response.data;
  },
};

// Summaries carry thumbnail_url (served by the API), full lessons the thumbnail itself as a data URL.
export function lessonThumbnailSrc(lesson: LessonSummary & { thumbnail?: string | null }): string | null {
  if (lesson.thumbnail_url) {
    return `${API_URL}${lesson.thumbnail_url}`;
  }
  return lesson.thumbnail || null;
}

export const lessonService = {
  // Summaries: no theory_content or starter_code.
  async list(): Promise<LessonSummary[]> {
    const response = await api.get('/api/lessons/');
    return response.data;
  },

  // The lessons of a class the user may see (for students only the visible assignments), as summaries.
  async listForClass(classId: number): Promise<LessonSummary[]> {
    const response = await api.get(`/api/lessons/class/${classId}`);
    return response.data;
  },

  async get(lessonId: number | string): Promise<Lesson> {
    const response = await api.get(`/api/lessons/${lessonId}`);
    return response.data;
  },

  async create(data: LessonCreate): Promise<Lesson> {
    const response = await api.post('/api/lessons/', data);
    return response.data;
  },

  async update(lessonId: number, data: LessonUpdate): Promise<Lesson> {
    const response = await api.patch(`/api/lessons/${lessonId}`, data);
    return response.data;
  },

  async remove(lessonId: number): Promise<void> {
    await api.delete(`/api/lessons/${lessonId}`);
  },

  async syncFromGitHub(lessonId: number): Promise<void> {
    await api.post(`/api/lessons/${lessonId}/sync`);
  },

  // WRK-21: the report becomes a GitHub issue for the lessons' maintainers (503 when reporting isn't set up, 429 beyond
  // the limits). Nothing comes back: the issue is in a private repository.
  async reportIssue(lessonId: number, report: LessonIssueReport): Promise<void> {
    await api.post(`/api/lessons/${lessonId}/issue-reports`, report);
  },

  // A short-lived token the simulation server needs to start this lesson's world, or null when the backend
  // has no secret configured (local development). It expires in minutes: request one right before each start.
  // Throws SimulationTimeUsedUp when today's simulation time is used up.
  // `classId`: the class the lesson was opened from; a paid class's daily class time pays for its lessons (PLAN-3).
  async simulationToken(lessonId: number, classId?: number | null): Promise<string | null> {
    try {
      const response = await api.post(`/api/lessons/${lessonId}/simulation-token`, classId ? { class_id: classId } : {});
      return response.data?.token ?? null;
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      // PLAN-11: the codes are a contract with the backend.
      if (error?.response?.status === 429 && (detail?.code === 'simulation_time_used_up' || detail?.code === 'class_time_used_up')) {
        throw new SimulationTimeUsedUp(detail.resets_at, detail.limit_seconds ?? null, detail.code === 'class_time_used_up' ? 'class' : 'personal', detail.plan);
      }
      throw error;
    }
  },
};

export class SimulationTimeUsedUp extends Error {
  // `source`: the user's own daily time, or a paid class's daily class time; `plan`: the user's plan (free, student).
  constructor(public resetsAt: string, public limitSeconds: number | null, public source: 'personal' | 'class' = 'personal', public plan?: string) {
    super("You've used today's simulation time.");
  }
}

export const simulationService = {
  // With a lesson and the class it was opened from: the time that pays for it, the class's or the user's own (SIM-17).
  async allowance(lessonId?: number, classId?: number | null): Promise<SimulationAllowance> {
    const response = await api.get('/api/simulations/allowance', { params: lessonId && classId ? { lesson_id: lessonId, class_id: classId } : {} });
    return response.data;
  },
};

export const billingService = {
  async me(): Promise<BillingInfo> {
    const response = await api.get('/api/billing/me');
    return response.data;
  },
  // Public: whether online payment is on (the front page's note while it isn't, PLAN-18).
  async status(): Promise<{ payments: boolean }> {
    const response = await api.get('/api/billing/status');
    return response.data;
  },
  // A free month of a plan while online payment is pending (PLAN-18): an email to the owner; the answer's message says so.
  async requestTrial(plan: 'student' | 'class', classId?: number, note?: string): Promise<string> {
    const response = await api.post('/api/billing/trial-requests', { plan, class_id: classId, note: note || undefined });
    return response.data.message;
  },
  // Admins, until Paddle checkout exists (PLAN-6). `trial`: a free month, which the user's email names (PLAN-19).
  async setStudentPlan(userId: number, active: boolean, periodEnd?: string, trial = false): Promise<void> {
    await api.put(`/api/billing/users/${userId}/student-plan`, { active, period_end: periodEnd, trial });
  },
  async setClassPlan(classId: number, active: boolean, periodEnd?: string, trial = false): Promise<void> {
    await api.put(`/api/billing/classes/${classId}/plan`, { active, period_end: periodEnd, trial });
  },
  async listClasses(): Promise<AdminClassPlan[]> {
    const response = await api.get('/api/billing/classes');
    return response.data;
  },
  // A short-lived link to the website's checkout page for this user and plan (and class) (PLAN-17); `returnPath` is the
  // app's page to come back to.
  async checkoutLink(plan: 'student' | 'class', classId: number | undefined, returnPath: string): Promise<string> {
    const response = await api.post('/api/billing/checkout-link', { plan, class_id: classId, return_path: returnPath });
    return response.data.url;
  },
  // A Paddle customer portal link (manage the Student plan).
  async portal(): Promise<string> {
    const response = await api.post('/api/billing/portal');
    return response.data.url;
  },
};

export const submissionService = {
  async submit(data: SubmissionCreate): Promise<Submission> {
    const response = await api.post('/api/submissions/', data);
    return response.data;
  },

  async mine(lessonId: number | string): Promise<Submission[]> {
    const response = await api.get('/api/submissions/my-submissions', { params: { lesson_id: lessonId } });
    return response.data;
  },

  async remove(submissionId: number): Promise<void> {
    await api.delete(`/api/submissions/${submissionId}`);
  },

  async getShared(shareToken: string): Promise<SharedSubmission> {
    const response = await api.get(`/api/submissions/share/${encodeURIComponent(shareToken)}`);
    return response.data;
  },

  async lessonStatus(lessonId: number): Promise<LessonStatus> {
    const response = await api.get(`/api/submissions/lesson/${lessonId}/status`);
    return response.data.status;
  },

  async forClassLesson(classId: number, lessonId: number): Promise<SubmissionWithStudent[]> {
    const response = await api.get(`/api/submissions/class/${classId}/lesson/${lessonId}`);
    return response.data;
  },

  async classProgress(classId: number): Promise<StudentProgress[]> {
    const response = await api.get(`/api/submissions/class/${classId}/progress`);
    return response.data;
  },

  async review(submissionId: number, teacherReview: string): Promise<Submission> {
    const response = await api.put(`/api/submissions/${submissionId}/review`, { teacher_review: teacherReview });
    return response.data;
  },

  // Rejects with a 404 when the student has no draft for the lesson yet.
  async getDraft(lessonId: number | string): Promise<LessonDraft> {
    const response = await api.get(`/api/submissions/lesson/${lessonId}/draft`);
    return response.data;
  },

  async saveDraft(lessonId: number | string, code: string): Promise<LessonDraft> {
    const response = await api.put(`/api/submissions/lesson/${lessonId}/draft`, { code });
    return response.data;
  },
};

// Lesson management service
export const lessonManagementService = {
  async getClassLessons(classId: number): Promise<LessonAssignment[]> {
    const response = await api.get(`/api/classes/${classId}/lessons`);
    return response.data;
  },

  async addLessonToClass(classId: number, data: LessonAssignmentCreate): Promise<LessonAssignment> {
    const response = await api.post(`/api/classes/${classId}/lessons`, data);
    return response.data;
  },

  async removeLessonFromClass(classId: number, lessonId: number): Promise<void> {
    await api.delete(`/api/classes/${classId}/lessons/${lessonId}`);
  },

  async updateLessonAssignment(
    classId: number,
    lessonId: number,
    data: LessonAssignmentUpdate
  ): Promise<LessonAssignment> {
    const response = await api.patch(`/api/classes/${classId}/lessons/${lessonId}`, data);
    return response.data;
  },

  async reorderLessons(
    classId: number,
    assignments: LessonAssignmentReorder[]
  ): Promise<void> {
    await api.post(`/api/classes/${classId}/lessons/reorder`, { assignments });
  },
};

export const userService = {
  async getUsers(): Promise<AdminUser[]> {
    const response = await api.get('/api/users/');
    return response.data;
  },

  async createUser(data: any): Promise<User> {
    const response = await api.post('/api/users/', data);
    return response.data;
  },

  async updateUser(userId: number, data: any): Promise<User> {
    const response = await api.patch(`/api/users/${userId}`, data);
    return response.data;
  },

  async deleteUser(userId: number): Promise<void> {
    await api.delete(`/api/users/${userId}`);
  },

  // A user's data export for a request that came by email (ACC-15).
  async exportUser(userId: number): Promise<Blob> {
    const response = await api.get(`/api/users/${userId}/export`, { responseType: 'blob' });
    return response.data;
  },
};

export default api;
